import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  Layers, 
  BarChart3, 
  Workflow, 
  CheckCircle,
  ArrowRight,
  Clock,
  Zap,
  Settings,
  Globe,
  Shield,
  Activity,
  TrendingUp,
  Users,
  FileText,
  AlertCircle,
  MonitorSpeaker,
  Gauge,
  Target,
  Bot,
  Monitor,
  Database,
  Building2
} from 'lucide-react';

const MultiPlatform = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const platformCapabilities = [
    {
      icon: <Bot className="w-6 h-6 text-primary" />,
      title: "Unified AI Dashboard", 
      description: "Single pane of glass for all AI-powered AP operations across multiple ERPs and entities.",
      features: ["Cross-system visibility", "Unified reporting", "Centralized alerts", "Global workflow management"]
    },
    {
      icon: <Workflow className="w-6 h-6 text-primary" />,
      title: "Intelligent Workflow Orchestration",
      description: "AI-driven workflow routing that adapts to your business rules across different systems and processes.",
      features: ["Dynamic routing logic", "Exception handling", "Approval hierarchies", "SLA monitoring"]
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-primary" />,
      title: "Advanced Analytics Engine",
      description: "Real-time analytics and predictive insights across your entire AP ecosystem with machine learning.",
      features: ["Predictive analytics", "Performance trending", "Cost optimization", "Risk assessment"]
    },
    {
      icon: <Globe className="w-6 h-6 text-primary" />,
      title: "Multi-Entity Management",
      description: "Seamlessly manage AP operations across multiple companies, currencies, and regulatory environments.",
      features: ["Multi-currency support", "Compliance management", "Entity consolidation", "Regional customization"]
    }
  ];

  const dashboardFeatures = [
    {
      category: "Executive Dashboard",
      description: "C-suite visibility into AP operations",
      metrics: [
        { name: "Days Payable Outstanding (DPO)", value: "32.5 days", trend: "down" },
        { name: "Processing Cost per Invoice", value: "$2.87", trend: "down" },
        { name: "Exception Rate", value: "4.2%", trend: "down" },
        { name: "Supplier Satisfaction Score", value: "94.7%", trend: "up" }
      ]
    },
    {
      category: "Operations Dashboard", 
      description: "Real-time operational metrics for AP teams",
      metrics: [
        { name: "Invoices Processed Today", value: "1,247", trend: "up" },
        { name: "AI Auto-Resolution Rate", value: "95.3%", trend: "up" },
        { name: "Average Processing Time", value: "12 mins", trend: "down" },
        { name: "Approval Queue Depth", value: "23", trend: "down" }
      ]
    }
  ];

  const workflowCapabilities = [
    {
      workflow: "Invoice Processing Automation",
      description: "End-to-end invoice lifecycle management",
      steps: [
        "AI Email Receipt & Classification",
        "Automated Data Extraction & Validation", 
        "ERP Integration & 3-Way Matching",
        "Intelligent Routing & Approval",
        "Payment Processing & Reconciliation"
      ],
      automation: "94%",
      timeReduction: "87%"
    },
    {
      workflow: "Exception Resolution Engine",
      description: "Intelligent handling of AP exceptions and disputes",
      steps: [
        "Exception Detection & Classification",
        "Contextual Data Gathering", 
        "Resolution Pathway Analysis",
        "Automated Resolution Execution",
        "Stakeholder Communication"
      ],
      automation: "78%", 
      timeReduction: "71%"
    },
    {
      workflow: "Supplier Communication Hub",
      description: "Centralized supplier interaction management",
      steps: [
        "Incoming Query Classification",
        "ERP Data Context Retrieval",
        "Intelligent Response Generation",
        "Escalation Management",
        "Relationship Analytics"
      ],
      automation: "Up to 90%",
      timeReduction: "92%"
    }
  ];

  const analyticsInsights = [
    {
      insight: "Predictive Cash Flow Management",
      description: "ML-powered forecasting of payment obligations and cash requirements",
      impact: "15% improvement in cash flow predictability",
      technical: "Time series analysis with seasonal adjustments and external factor correlation"
    },
    {
      insight: "Supplier Risk Assessment",
      description: "Continuous monitoring of supplier financial health and delivery performance", 
      impact: "32% reduction in supply chain disruptions",
      technical: "Multi-factor risk scoring with external data integration and trend analysis"
    },
    {
      insight: "Process Optimization Recommendations",
      description: "AI-driven suggestions for workflow improvements and cost reductions",
      impact: "23% operational efficiency improvement",
      technical: "Process mining algorithms with bottleneck identification and optimization modeling"
    },
    {
      insight: "Compliance Monitoring & Alerts",
      description: "Automated tracking of regulatory requirements and internal policy adherence",
      impact: "98% compliance score maintenance",
      technical: "Rule engine with regulatory change monitoring and proactive alert generation"
    }
  ];

  const integrationStats = [
    {
      metric: "99.9%",
      description: "Platform Uptime",
      detail: "Enterprise SLA with redundancy"
    },
    {
      metric: "8+ ERPs",
      description: "Native Integrations",
      detail: "Oracle EBS + Fusion, NetSuite, SAP, Dynamics, Yardi, PeopleSoft, JDE"
    },
    {
      metric: "88-92%",
      description: "Touchless Processing",
      detail: "Invoices needing zero human touch"
    },
    {
      metric: "Real-Time",
      description: "Data Synchronization",
      detail: "Cross-platform consistency"
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Multi-Platform Integration Hub - Unified AP Management | EZ Cloud"
        description="Comprehensive AP automation platform unifying multiple ERPs and entities. Real-time analytics, intelligent workflows, and multi-entity management capabilities."
        canonical="https://ezcloud.ai/technology/multi-platform"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Platform', url: 'https://ezcloud.ai/#platform' },
          { name: 'Multi-Platform', url: 'https://ezcloud.ai/technology/multi-platform' },
        ]}
      />
      <Navigation />
      <Breadcrumbs />
      
      {/* Hero Section */}
      <section className="pt-20 pb-20 bg-hero-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-primary/10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl -translate-x-48 -translate-y-48"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl translate-x-48 translate-y-48"></div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-accent text-accent-foreground px-6 py-2 text-lg">Unified Platform</Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Multi-Platform
              <span className="block text-accent">Integration Hub</span>
            </h1>
            <p className="text-xl lg:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
              Comprehensive AP automation platform that unifies multiple ERPs, entities, and processes 
              into a single, intelligent management system.
            </p>
            
            {/* Key Platform Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">100+</div>
                <div className="text-sm text-white/80">Configurable Workflows</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">Real-Time</div>
                <div className="text-sm text-white/80">Cross-System Analytics</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">Multi-Entity</div>
                <div className="text-sm text-white/80">Global Management</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">24/7</div>
                <div className="text-sm text-white/80">Intelligent Monitoring</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-4 bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => openCalendlyDemo()}
              >
                <Layers className="w-5 h-5 mr-2" />
                Explore Platform Demo
              </Button>
              <Button asChild size="lg" variant="outline-on-dark" className="text-lg px-8 py-4">
                <Link to="/technology/ai-email-prevention">
                  View AI Technology
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Capabilities Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
              Platform Capabilities
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive AP automation capabilities designed to work seamlessly across multiple systems and business processes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {platformCapabilities.map((capability, index) => (
              <Card key={index} className="border-none shadow-large hover:shadow-xl transition-spring bg-white">
                <CardHeader>
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="flex-shrink-0 p-3 bg-primary/10 rounded-lg">
                      {capability.icon}
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-foreground">{capability.title}</CardTitle>
                      <p className="text-muted-foreground mt-2">{capability.description}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {capability.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Dashboard Preview */}
      <section className="py-20 bg-section-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Unified Dashboard Experience
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Real-time visibility across your entire AP ecosystem with role-based dashboards for different stakeholders.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {dashboardFeatures.map((dashboard, index) => (
              <Card key={index} className="border-none shadow-large bg-white">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-foreground flex items-center">
                    <Gauge className="w-5 h-5 mr-2 text-primary" />
                    {dashboard.category}
                  </CardTitle>
                  <p className="text-muted-foreground">{dashboard.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {dashboard.metrics.map((metric, metricIndex) => (
                      <div key={metricIndex} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-semibold text-foreground">{metric.name}</div>
                          <div className="text-2xl font-bold text-accent">{metric.value}</div>
                        </div>
                        <div className="flex items-center">
                          <TrendingUp 
                            className={`w-5 h-5 ${
                              metric.trend === 'up' ? 'text-green-500' : 'text-red-500'
                            } ${metric.trend === 'up' ? '' : 'rotate-180'}`} 
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Automation Deep Dive */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Intelligent Workflow Automation
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              AI-powered workflows that adapt to your business processes and continuously optimize for efficiency.
            </p>
          </div>

          <div className="space-y-12 max-w-6xl mx-auto">
            {workflowCapabilities.map((workflow, index) => (
              <Card key={index} className="border-none shadow-large bg-white overflow-hidden">
                <CardHeader className="bg-primary/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl font-bold text-foreground">{workflow.workflow}</CardTitle>
                      <p className="text-muted-foreground">{workflow.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-accent">{workflow.automation}</div>
                      <div className="text-sm text-muted-foreground">Automation Rate</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-5 gap-4 mb-6">
                    {workflow.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="text-center">
                        <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center text-white font-bold mx-auto mb-2">
                          {stepIndex + 1}
                        </div>
                        <div className="text-sm text-foreground font-medium">{step}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-center space-x-8 text-sm text-muted-foreground">
                    <div className="flex items-center">
                      <Zap className="w-4 h-4 text-accent mr-1" />
                      {workflow.timeReduction} Time Reduction
                    </div>
                    <div className="flex items-center">
                      <Target className="w-4 h-4 text-accent mr-1" />
                      {workflow.automation} Automated
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Advanced Analytics & Insights */}
      <section className="py-20 bg-section-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Advanced Analytics & AI Insights
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Machine learning-powered analytics that provide predictive insights and continuous optimization recommendations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {analyticsInsights.map((analytic, index) => (
              <Card key={index} className="border-none shadow-large hover:shadow-xl transition-spring bg-white">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-foreground flex items-center">
                    <BarChart3 className="w-5 h-5 mr-2 text-primary" />
                    {analytic.insight}
                  </CardTitle>
                  <p className="text-muted-foreground">{analytic.description}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-accent/10 rounded-lg p-4 border-l-4 border-accent">
                    <div className="font-semibold text-foreground mb-1">Business Impact</div>
                    <div className="text-accent font-bold">{analytic.impact}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="font-semibold text-foreground mb-1">Technical Approach</div>
                    <div className="text-sm text-muted-foreground">{analytic.technical}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Performance Metrics */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Platform Performance Metrics
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Enterprise-grade performance standards with comprehensive monitoring and optimization.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto mb-16">
            {integrationStats.map((stat, index) => (
              <Card key={index} className="border-none shadow-large bg-white text-center p-6">
                <div className="text-4xl font-bold text-accent mb-2">{stat.metric}</div>
                <div className="text-lg font-semibold text-foreground mb-2">{stat.description}</div>
                <div className="text-sm text-muted-foreground">{stat.detail}</div>
              </Card>
            ))}
          </div>

          {/* Multi-Platform Integration Architecture */}
          <div className="bg-section-gradient rounded-lg p-8">
            <h3 className="text-2xl font-bold text-foreground mb-8 text-center">Multi-Platform Integration Architecture</h3>
            <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              Unified platform that orchestrates AP operations across multiple ERPs with intelligent analytics and automation
            </p>
            
            <div className="relative">
              {/* Flow Steps */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 relative">
                {/* Step 1: Unified Dashboard */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-primary/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-56 flex flex-col">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl mb-4 mx-auto group-hover:bg-primary/20 transition-colors">
                      <Monitor className="w-6 h-6 text-primary" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">Unified Dashboard</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4 flex-grow">Single view across all systems & entities</p>
                    <div className="text-xs text-center text-primary font-medium mt-auto">Real-Time</div>
                  </div>
                  {/* Connection Line */}
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-primary to-accent transform -translate-y-1/2">
                    <div className="absolute -right-1 top-1/2 w-2 h-2 bg-primary rounded-full transform -translate-y-1/2"></div>
                  </div>
                </div>

                {/* Step 2: Workflow Orchestrator */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-accent/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-56 flex flex-col">
                    <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-xl mb-4 mx-auto group-hover:bg-accent/20 transition-colors">
                      <Workflow className="w-6 h-6 text-accent" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">Workflow Orchestrator</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4 flex-grow">Intelligent routing & process management</p>
                    <div className="text-xs text-center text-accent font-medium mt-auto">Automated</div>
                  </div>
                  {/* Connection Line */}
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-accent to-secondary transform -translate-y-1/2">
                    <div className="absolute -right-1 top-1/2 w-2 h-2 bg-accent rounded-full transform -translate-y-1/2"></div>
                  </div>
                </div>

                {/* Step 3: AI Analytics Engine */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-primary/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-56 flex flex-col">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl mb-4 mx-auto group-hover:bg-primary/20 transition-colors">
                      <Bot className="w-6 h-6 text-primary" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">AI Analytics Engine</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4 flex-grow">Machine learning insights & predictions</p>
                    <div className="text-xs text-center text-primary font-medium mt-auto">Predictive</div>
                  </div>
                  {/* Connection Line */}
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-primary to-accent transform -translate-y-1/2">
                    <div className="absolute -right-1 top-1/2 w-2 h-2 bg-primary rounded-full transform -translate-y-1/2"></div>
                  </div>
                </div>

                {/* Step 4: Cross-System Data Layer */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-primary/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-56 flex flex-col">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl mb-4 mx-auto group-hover:bg-primary/20 transition-colors">
                      <Database className="w-6 h-6 text-primary" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">Data Layer</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4 flex-grow">Unified data model & synchronization</p>
                    <div className="text-xs text-center text-primary font-medium mt-auto">Synchronized</div>
                  </div>
                  {/* Connection Line */}
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-primary to-accent transform -translate-y-1/2">
                    <div className="absolute -right-1 top-1/2 w-2 h-2 bg-primary rounded-full transform -translate-y-1/2"></div>
                  </div>
                </div>

                {/* Step 5: ERP Integrations & BI */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-accent/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-56 flex flex-col">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-accent to-primary rounded-xl mb-4 mx-auto group-hover:scale-110 transition-transform">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">ERP & BI Integration</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4 flex-grow">Native connections & business intelligence</p>
                    <div className="text-xs text-center text-accent font-medium mt-auto">Enterprise</div>
                  </div>
                </div>
              </div>

              {/* Mobile Flow Connectors */}
              <div className="lg:hidden flex flex-col items-center space-y-4 absolute top-0 left-1/2 transform -translate-x-1/2 h-full">
                <div className="w-0.5 bg-gradient-to-b from-primary via-accent via-secondary to-primary h-full opacity-30"></div>
              </div>

              {/* Platform Stats */}
              <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">100+</div>
                  <div className="text-xs text-muted-foreground">Configurable Workflows</div>
                </div>
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-2xl font-bold text-accent mb-1">Multi-Entity</div>
                  <div className="text-xs text-muted-foreground">Global Management</div>
                </div>
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">Real-Time</div>
                  <div className="text-xs text-muted-foreground">Analytics & Sync</div>
                </div>
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">24/7</div>
                  <div className="text-xs text-muted-foreground">Intelligent Monitoring</div>
                </div>
              </div>

              {/* Platform Benefits */}
              <div className="mt-8 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl p-6">
                <h4 className="text-lg font-bold text-center mb-4">Unified Platform Benefits</h4>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2 mx-auto">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="text-sm font-medium">Single Source of Truth</div>
                    <div className="text-xs text-muted-foreground">Unified data across all systems</div>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2 mx-auto">
                      <TrendingUp className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="text-sm font-medium">Intelligent Automation</div>
                    <div className="text-xs text-muted-foreground">AI-powered process optimization</div>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-2 mx-auto">
                      <Shield className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="text-sm font-medium">Enterprise Security</div>
                    <div className="text-xs text-muted-foreground">Comprehensive data protection</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED RESOURCES */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                Related EZ Cloud platform & industry resources
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Where the multi-platform hub fits with the AI Email Prevention layer, native ERP integration, and industry-specific deployments.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/technology/ai-email-prevention"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  AI Email Prevention
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  The exception-prevention layer that runs above your ERP integrations — resolves 90% of supplier issues without AP staff involvement.
                </p>
              </Link>

              <Link
                to="/technology/native-erp"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Native ERP integration
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Real-time bi-directional sync, sub-100ms latency, SOC 2 compliance — the layer underneath the multi-platform hub.
                </p>
              </Link>

              <Link
                to="/integrations"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  All ERP integrations
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Five prebuilt native integrations — Oracle, NetSuite, SAP, Dynamics 365, Yardi.
                </p>
              </Link>

              <Link
                to="/industries/manufacturing"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Manufacturing AP
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Multi-plant, multi-entity manufacturing AP — where the multi-platform hub centralizes operations across ERP instances.
                </p>
              </Link>

              <Link
                to="/customers"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Customer stories
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Real outcomes from EZ Cloud customers running multi-entity, multi-ERP operations on a unified AI platform.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-hero-gradient text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Transform Your AP Operations
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Experience the power of unified AP automation across all your systems. 
              See how our multi-platform approach can streamline your entire accounts payable operation.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-4 bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => openCalendlyDemo()}
              >
                <Layers className="w-5 h-5 mr-2" />
                Schedule Platform Demo
              </Button>
              <Button asChild size="lg" variant="outline-on-dark" className="text-lg px-8 py-4">
                <Link to="/integrations">
                  View Integration Options
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
            
            <p className="text-sm text-white/70">
              🚀 Multi-ERP support • 📊 Real-time analytics • 🔄 Intelligent automation • 🛡️ Enterprise security
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MultiPlatform;